import React, { useState } from "react";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import { useSocial } from "../../contexts/SocialContext";
import { Loader2, PenLine } from "lucide-react";
import DetailSheet from "../session/generate/DetailSheet";
import { Field, OptionList } from "../profile/ProfileParts";

/**
 * Posting by hand: a button that opens a sheet with the message, who sees it,
 * and Post where Done would be. Most workouts post themselves (issue #79);
 * this is for a note, or for people who switched that off.
 */
export default function ShareWorkoutDialog({ workoutSession, planData, onShared, label = "Post", className }) {
  const { createPost, groups } = useSocial();
  const [open, setOpen] = useState(false);
  const [content, setContent] = useState("");
  const [selectedGroup, setSelectedGroup] = useState("");
  const [sharing, setSharing] = useState(false);

  const hasWorkout = Boolean(workoutSession || planData);

  const handleOpenChange = (next) => {
    setOpen(next);
    if (!next) {
      setContent("");
      setSelectedGroup("");
    }
  };

  const handleShare = async () => {
    setSharing(true);
    try {
      await createPost({
        content: content.trim() || null,
        workout_session_id: workoutSession?.id || null,
        plan_data: planData || workoutSession?.plan_data || null,
        group_id: selectedGroup || null,
        visibility: selectedGroup ? "group" : "friends",
      });
      handleOpenChange(false);
      onShared?.();
    } catch (err) {
      console.error("Share failed:", err);
    } finally {
      setSharing(false);
    }
  };

  const audience = [
    { value: "", label: "My mates", description: "Everyone you've accepted as a mate." },
    ...groups.map((g) => ({
      value: g.id,
      label: g.name,
      description: "Only the people in this group.",
    })),
  ];

  return (
    <>
      <Button variant="outline" className={className} onClick={() => setOpen(true)}>
        <PenLine className="mr-2 h-4 w-4" /> {label}
      </Button>
      <DetailSheet
        open={open}
        onOpenChange={handleOpenChange}
        title={hasWorkout ? "Post this workout" : "Post to the feed"}
        footer={
          <Button
            className="h-12 w-full text-base font-semibold"
            onClick={handleShare}
            disabled={sharing || (!hasWorkout && !content.trim())}
          >
            {sharing && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Post
          </Button>
        }
      >
        <Field label="Message" htmlFor="post-message" aside={hasWorkout ? "Optional" : undefined}>
          <Textarea
            id="post-message"
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder="What did you get up to?"
            rows={3}
          />
        </Field>
        {groups.length > 0 && (
          <Field label="Who sees it">
            <OptionList options={audience} value={selectedGroup} onChange={setSelectedGroup} />
          </Field>
        )}
      </DetailSheet>
    </>
  );
}
